import {
  ConnectionConfiguration,
  ConnectionConfigurationCreate,
  ConnectionCreate,
  Connection,
  PostgreSQLConnectionConfigurationBE,
} from "src/api/connectApi/types";
import {
  filterSecretsByEnvironment,
  filterUpdatedSecrets,
  findSecret,
  getDefaultSecret,
  markSecrets,
  setSecretDeleted,
  setSecretEnv,
} from "src/connections/model/common";
import {
  KeyValuePair,
  PostgreSQLConnectionConfigInputs,
  PostgreSQLConnectionConfig,
} from "src/connections/types";

export const getPostgreSQLConnectionConfigDefaultValues = (
  env: string | null,
): PostgreSQLConnectionConfig => ({
  databaseName: "",
  host: "",
  port: null,
  allowTestInvocations: false,
  authMethod: "username_password",
  hasMTLSEnabled: false,
  mTLSConfig: {
    mode: "verify-full",
    clientCertificate: "",
    clientKey: getDefaultSecret("client_key", env),
    CACertificate: "",
  },
  usernamePasswordConfig: {
    username: getDefaultSecret("username", env),
    password: getDefaultSecret("password", env),
  },
  SSHTunnel: {
    host: "",
    port: 22,
    user: "",
    privateKey: getDefaultSecret("ssh_private_key", env),
  },
  hasSSHTunnelEnabled: false,
});

export const getPostgreSQLConnectionConfigInputsDefaultValues =
  (): PostgreSQLConnectionConfigInputs => ({
    name: "",
    dataRetention: {
      value: 0,
      unit: "days",
    },
    enableNonProdConfigs: false,
    productionConfig: getPostgreSQLConnectionConfigDefaultValues(null),
    sandboxConfig: getPostgreSQLConnectionConfigDefaultValues("sandbox"),
  });

const convertBEConnectionConfigurationToPostgreSQLConnectionConfig = (
  config: ConnectionConfiguration,
  envSecrets: KeyValuePair[],
  env: string | null,
): PostgreSQLConnectionConfig => {
  const postgresConfig = config as PostgreSQLConnectionConfigurationBE;
  return {
    databaseName: postgresConfig.database_name,
    host: postgresConfig.host,
    port: postgresConfig.port,
    allowTestInvocations: config.allow_test_invocations ?? false,
    hasMTLSEnabled: postgresConfig.has_mtls_enabled ?? false,
    mTLSConfig: {
      mode: postgresConfig.mutual_tls?.mode ?? "verify-full",
      clientCertificate: postgresConfig.mutual_tls?.client_certificate ?? "",
      clientKey:
        findSecret(envSecrets, "client_key") ??
        getDefaultSecret("client_key", env),
      CACertificate: postgresConfig.mutual_tls?.ca_certificate ?? "",
    },
    authMethod: postgresConfig.auth_method,
    usernamePasswordConfig: {
      username:
        findSecret(envSecrets, "username") ?? getDefaultSecret("username", env),
      password:
        findSecret(envSecrets, "password") ?? getDefaultSecret("password", env),
    },
    SSHTunnel: {
      host: postgresConfig.ssh_tunnel?.host ?? "",
      port: postgresConfig.ssh_tunnel?.port ?? 22,
      user: postgresConfig.ssh_tunnel?.user ?? "",
      privateKey:
        findSecret(envSecrets, "ssh_private_key") ??
        getDefaultSecret("ssh_private_key", env),
    },
    hasSSHTunnelEnabled: postgresConfig.has_ssh_tunnel_enabled ?? false,
  };
};

const convertBENonProdEnvConfigToPostgreSQLConnectionConfigs = (
  nonProdEnvConfigs: Record<string, ConnectionConfiguration>,
  env: string,
  allSecrets: KeyValuePair[],
): PostgreSQLConnectionConfig | null => {
  if (!nonProdEnvConfigs?.[env]) return null;
  const envSecrets = filterSecretsByEnvironment(allSecrets, env);
  return convertBEConnectionConfigurationToPostgreSQLConnectionConfig(
    nonProdEnvConfigs[env],
    envSecrets,
    env,
  );
};

/**
 * Converts BE PostgreSQL connection configuration to FE format.
 * If the provider of the connection is not "postgresql", it throws an error.
 *
 * @param {Connection} connection - The PostgreSQL connection object in BE format.
 * @returns {PostgreSQLConnectionConfigInputs} - The converted PostgreSQL connection object in FE format suitable to use with components.
 * @throws {Error} - Throws an error if the provider of the connection is not "postgresql".
 */
export const convertBEConnectionToPostgreSQLConnectionConfigInputs = (
  connection: Connection,
): PostgreSQLConnectionConfigInputs => {
  if (connection.provider !== "postgresql")
    throw new Error(
      `Invalid connection type: ${connection.provider} for PostgreSQL converter`,
    );

  const defaultValues = getPostgreSQLConnectionConfigInputsDefaultValues();
  const nonProdEnvConfigs = connection.non_prod_env_configs;

  const allSecrets = markSecrets(connection.secrets) ?? [];
  const prodSecrets = filterSecretsByEnvironment(allSecrets, null);
  return {
    ...defaultValues,
    name: connection.name,
    dataRetention: connection.data_retention ?? defaultValues.dataRetention,
    enableNonProdConfigs: connection.enable_non_prod_configs,
    productionConfig:
      convertBEConnectionConfigurationToPostgreSQLConnectionConfig(
        connection.configuration,
        prodSecrets,
        null,
      ),
    sandboxConfig:
      nonProdEnvConfigs && nonProdEnvConfigs["sandbox"]
        ? convertBENonProdEnvConfigToPostgreSQLConnectionConfigs(
            nonProdEnvConfigs,
            "sandbox",
            allSecrets,
          )
        : getPostgreSQLConnectionConfigDefaultValues("sandbox"),
  };
};

const extractSecretsFromPostgreSQLConnectionConfig = (
  config: PostgreSQLConnectionConfig,
  env: string | null,
): KeyValuePair[] => {
  if (config.authMethod === "username_password") {
    return [
      setSecretEnv(config.usernamePasswordConfig.username, env),
      setSecretEnv(config.usernamePasswordConfig.password, env),
    ];
  }
  return [];
};

const extractSecretsFromPostgreSQLConnectionMTLSConfig = (
  config: PostgreSQLConnectionConfig,
  env: string | null,
): KeyValuePair[] => {
  if (config.hasMTLSEnabled) {
    return [setSecretEnv(config.mTLSConfig.clientKey, env)];
  } else {
    // If mTLS is disabled, we need to delete the secrets that are present
    return [config.mTLSConfig.clientKey]
      .filter((secret) => secret.value !== "")
      .map((s) => setSecretEnv(s, env))
      .map(setSecretDeleted);
  }
};

const extractSecretsFromPostgreSQLConnectionSSHTunnelConfig = (
  config: PostgreSQLConnectionConfig,
  env: string | null,
): KeyValuePair[] => {
  if (config.hasSSHTunnelEnabled && config.SSHTunnel) {
    return [setSecretEnv(config.SSHTunnel.privateKey, env)];
  }
  if (config.SSHTunnel) {
    return [config.SSHTunnel.privateKey]
      .filter((secret) => secret?.value !== "")
      .map((s) => setSecretEnv(s, env))
      .map(setSecretDeleted);
  }
  return [];
};

const extractSecretsFromPostgreSQLConnectionConfigInputs = (
  config: PostgreSQLConnectionConfigInputs,
): KeyValuePair[] => {
  const secrets = extractSecretsFromPostgreSQLConnectionConfig(
    config.productionConfig,
    null,
  );
  secrets.push(
    ...extractSecretsFromPostgreSQLConnectionMTLSConfig(
      config.productionConfig,
      null,
    ),
  );
  secrets.push(
    ...extractSecretsFromPostgreSQLConnectionSSHTunnelConfig(
      config.productionConfig,
      null,
    ),
  );

  // Only include non-prod secrets if non-prod configs are enabled
  if (config.enableNonProdConfigs && config.sandboxConfig) {
    secrets.push(
      ...extractSecretsFromPostgreSQLConnectionConfig(
        config.sandboxConfig,
        "sandbox",
      ),
    );
    secrets.push(
      ...extractSecretsFromPostgreSQLConnectionMTLSConfig(
        config.sandboxConfig,
        "sandbox",
      ),
    );
    secrets.push(
      ...extractSecretsFromPostgreSQLConnectionSSHTunnelConfig(
        config.sandboxConfig,
        "sandbox",
      ),
    );
  }

  // Removing un-changed secrets, so that they are not sent to the backend
  return filterUpdatedSecrets(secrets);
};

const convertPostgreSQLConnectionConfigToBEConnectionConfiguration = (
  config: PostgreSQLConnectionConfig,
): ConnectionConfigurationCreate => {
  return {
    database_name: config.databaseName,
    host: config.host,
    port: config.port || null, // Replace "" with null when port is deleted
    auth_method: config.authMethod,
    allow_test_invocations: config.allowTestInvocations,
    has_mtls_enabled: config.hasMTLSEnabled,
    mutual_tls: {
      mode: config.mTLSConfig.mode,
      client_certificate: config.mTLSConfig.clientCertificate,
      ca_certificate: config.mTLSConfig.CACertificate,
    },
    has_ssh_tunnel_enabled: config.hasSSHTunnelEnabled,
    ssh_tunnel: {
      host: config.SSHTunnel?.host ?? "",
      port: config.SSHTunnel?.port ?? 22,
      user: config.SSHTunnel?.user ?? "",
    },
  };
};

/**
 * Converts FE PostgreSQL connection object to BE format.
 *
 * @param {PostgreSQLConnectionConfigInputs} inputs - The PostgreSQL connection object in FE format.
 * @returns {ConnectionCreate} - The PostgreSQL connection object in BE format suitable for sending POST request.
 */
export const convertPostgreSQLConnectionConfigInputsToBEConnection = (
  inputs: PostgreSQLConnectionConfigInputs,
): ConnectionCreate => {
  const secrets = extractSecretsFromPostgreSQLConnectionConfigInputs(inputs);

  return {
    name: inputs.name,
    is_sandbox: false,
    provider: "postgresql",
    configuration: {
      ...convertPostgreSQLConnectionConfigToBEConnectionConfiguration(
        inputs.productionConfig,
      ),
    },
    secrets,
    data_retention: inputs.dataRetention,
    enable_non_prod_configs: inputs.enableNonProdConfigs,
    non_prod_env_configs:
      inputs.enableNonProdConfigs && inputs.sandboxConfig
        ? {
            sandbox:
              convertPostgreSQLConnectionConfigToBEConnectionConfiguration(
                inputs.sandboxConfig,
              ),
          }
        : {},
  };
};
